<script lang="ts">
  // show list of Centers
  // use modals for new, edit and delete
  // new page for results

  import { db } from "../firebase";
  import { collection, query, orderBy, onSnapshot } from "@firebase/firestore";

  import type { Unsubscribe } from "@firebase/firestore";

  import { onMount, onDestroy } from "svelte";

  import { Pages, DocType } from "../enums";
  import type { Center } from "../types";

  import { Icons } from "../utilities/icons";

  import { route } from "../stores/route.svelte";

  import IconButton from "../components/IconButton.svelte";
  import ListBox from "../components/ListBox.svelte";
  import EditCenterModal from "../modals/EditCenterModal.svelte";
  import NewCenterModal from "../modals/NewCenterModal.svelte";

  // STATE -----------------------------------------------------------

  let unsubscribe: Unsubscribe;
  let centers: Array<Center> = $state([]);

  // list of users to display, construct from firestore database
  let listItems: Array<string> = $state([]);
  // selected in list
  let selectedIndex: number | null = $state(null);

  let showEditCenterModal = $state(false);
  let showNewCenterModal = $state(false);

  // FUNCTIONS -------------------------------------------------------

  // database operations ---

  const getRealtimeUpdates = () => {
    const collectionRef = collection(db, DocType.Centers);
    const collectionQuery = query(collectionRef, orderBy("name"));
    unsubscribe = onSnapshot(collectionQuery, (querySnapshot) => {
      centers = [];
      listItems = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        centers.push({
          uid: doc.id,
          name: data.name,
          maxActiveUsers: data.maxActiveUsers,
        });
        listItems.push(data.name + " - " + data.maxActiveUsers + " borgere");
      });
    });
  };

  // button clicks ---

  const backButtonClicked = () => {
    route.current = Pages.AdminStart;
  };

  const newCenterButtonClicked = () => {
    showNewCenterModal = true;
  };

  const editCenterButtonClicked = () => {
    showEditCenterModal = true;
  };

  // item list functions ---

  const listItemSelected = (event: CustomEvent): void => {
    if (selectedIndex !== null) {
      selectedIndex = event.detail.index;
    }
  };

  // LIFE CYCLE --------------------------------------------------

  onMount(() => {
    getRealtimeUpdates();
  });

  onDestroy(() => {
    unsubscribe();
  });
</script>

<!-- HTML ===================================================== -->

<main data-page="center-edit">
  <div class="heading">
    <h1>Administration af Centre</h1>
  </div>

  <ListBox bind:items={listItems} bind:selectedIndex />

  <div class="bottom-area">
    <span class="button-spacing">
      <IconButton
        onclick={backButtonClicked}
        textMediumScaled={true}
        icon={Icons.ArrowLeft}
      >
        {#snippet message()}Tilbage{/snippet}
      </IconButton>
    </span>
    <span class="button-spacing">
      <IconButton
        onclick={newCenterButtonClicked}
        textMediumScaled={true}
        icon={Icons.Plus}
      >
        {#snippet message()}Ny{/snippet}
      </IconButton>
    </span>
    <span class="button-spacing">
      <IconButton
        disabled={selectedIndex === null}
        onclick={editCenterButtonClicked}
        textMediumScaled={true}
        icon={Icons.CogLight}
      >
        {#snippet message()}Rediger{/snippet}
      </IconButton>
    </span>
  </div>
</main>

{#if showEditCenterModal}
  <EditCenterModal
    editCenterDialogClose={() => {
      showEditCenterModal = false;
    }}
    aCenter={centers[selectedIndex]}
  />
{/if}

{#if showNewCenterModal}
  <NewCenterModal
    newCenterDialogClose={() => {
      showNewCenterModal = false;
    }}
  />
{/if}

<!-- CSS ===================================================== -->
<style>
  main {
    height: calc(var(--vh, 1vh) * 100);
    display: flex;
    flex-direction: column;
    padding: 1vmin;
  }
  h1 {
    font-size: 3vmin;
  }
  .heading {
    margin-bottom: 1vmin;
  }
  .bottom-area {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    border-top: 2px solid var(--key-hover-border-color);
    margin-top: 1vmin;
  }
  .button-spacing {
    padding-top: 1vmin;
    padding-right: 1vmin;
  }
  .log-out-button {
    margin-left: auto;
  }
</style>
